import { MouseEventHandler, useCallback } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, Typography } from "@mui/material";

import { RoleEnum } from "../../../common/models/enums";

import { ModelType } from "../../../API";
import { MediaFullScreen } from "../../../common/components/media/MediaFullScreen";
import TableCellTruncated from "../../../common/components/table/TableCellTruncated";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { UpdateEventFeedbackInput } from "../../../common/types/events/api";
import {
  formatTimestamp,
  formatTimestampInTimezone,
} from "../../../common/utils/timestampUtils";
import { getTimezoneFromLocationCache } from "../../../common/utils/timezoneUtils";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocation } from "../../../common/variables/selectedLocation";
import { useSelectedModel } from "../../../common/variables/selectedModel";
import FeedbackDetailsTableCellContainer from "../components/feedback/FeedbackDetailsTableCellContainer";
import FeedbackThumbsTableCellContainer from "../components/feedback/FeedbackThumbsTableCellContainer";
import { EventTableColumn } from "../components/table/EventTable";
import { EventViewModel } from "../models/eventViewModel";
import { RecordingViewModel } from "../models/recordingViewModel";
import { useUpdateEventFeedback } from "./events/useUpdateEventFeedback";
import { useFeedbackUpdateConfirmDialog } from "./useFeedbackUpdateConfirmDialog";
import { useHasRoleAccess } from "../../../common/hooks/useCheckRoleAccess";
import {
  DEFAULT_MEDIA_ICON_STYLES,
  modelToServiceMap,
} from "../../../common/variables/common";

export const useEventLeakTableColumns = (
  newestRecording?: RecordingViewModel,
  onMoreActionsClick?: MouseEventHandler<HTMLButtonElement>
) => {
  const customerId = useCustomerIdGuard();
  const modelId = useSelectedModel();
  const facilityId = useSelectedLocation();

  const isReadOnly = useHasRoleAccess("readonly");
  const isVerifier = useHasRoleAccess(RoleEnum.VERIFIER);

  const { updateEventFeedback, loading, error } = useUpdateEventFeedback({
    customerId,
    modelId: modelId?.value ?? "",
    facilityId: facilityId?.value ?? "",
  });

  const { getCachedLocationName } = useLocationFromCache();

  const handleFeedbackUpdate = useFeedbackUpdateConfirmDialog(
    updateEventFeedback,
    error
  );

  // Core columns that are always shown
  const coreColumns: EventTableColumn<EventViewModel>[] = [
    {
      title: "Event",
      width: "360px",
      content: event => {
        return (
          <Box
            sx={{
              display: "flex",
              width: "360px",
              alignItems: "center",
              gap: "0.75em",
            }}
          >
            <MediaFullScreen
              keyFrame={newestRecording?.keyFrame}
              mediaOutput={newestRecording?.mediaOutput}
              s3ImageFullScreenProps={{
                sx: DEFAULT_MEDIA_ICON_STYLES,
                fixedHeight: "75px",
              }}
            />

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="buttonMedium">
                {modelToServiceMap[event.modelId.toLowerCase() as ModelType]}
              </Typography>
              <Typography variant="body2Regular" color="text.secondary">
                Local:{" "}
                {formatTimestamp(newestRecording?.timestamp ?? event.timestamp)}
              </Typography>
              {(() => {
                const facilityTimezone = getTimezoneFromLocationCache(
                  event.location
                );

                return facilityTimezone ? (
                  <Typography variant="body2Regular" color="text.secondary">
                    Facility:{" "}
                    {formatTimestampInTimezone(
                      newestRecording?.timestamp ?? event.timestamp,
                      facilityTimezone
                    )}
                  </Typography>
                ) : null;
              })()}
            </Box>
          </Box>
        );
      },
    },
    {
      title: "Location",
      width: "160px",
      content: event => {
        const locationName = getCachedLocationName(event.location);

        return (
          <TableCellTruncated
            tooltipText={locationName}
            text={locationName}
            TypographyProps={{ variant: "body2" }}
          />
        );
      },
    },
  ];

  // Feedback columns (only for non-VERIFIER users)
  const feedbackColumns: EventTableColumn<EventViewModel>[] = isVerifier
    ? []
    : [
        {
          title: "Feedback",
          width: "140px",
          content: event => {
            const onUpdateEventFeedback = useCallback(
              (input: UpdateEventFeedbackInput) =>
                handleFeedbackUpdate(input, event),
              [handleFeedbackUpdate, event]
            );

            return (
              <FeedbackThumbsTableCellContainer
                event={event}
                onUpdateEventFeedback={onUpdateEventFeedback}
              />
            );
          },
        },
        {
          title: "Feedback details",
          width: "300px",
          content: event => {
            const memoizedCallback = useCallback(
              async (input: UpdateEventFeedbackInput) => {
                const result = await updateEventFeedback(input);

                if (error || !result) {
                  errorNotification("Something went wrong");
                } else {
                  successNotification(
                    "Event feedback details updated successfully"
                  );
                }
              },
              [updateEventFeedback, error]
            );

            return (
              <FeedbackDetailsTableCellContainer
                loading={loading}
                event={event}
                onUpdateEventFeedback={memoizedCallback}
              />
            );
          },
        },
      ];

  // Data columns that are always shown
  const dataColumns: EventTableColumn<EventViewModel>[] = [
    {
      title: "Equipment classification",
      width: "220px",
      content: event => {
        const equipmentClassification =
          !event.equipmentClassification ||
          event.equipmentClassification === "unknown"
            ? "Unknown"
            : event.equipmentClassification;

        return (
          <Typography variant="body2">{equipmentClassification}</Typography>
        );
      },
    },
    {
      title: "Source",
      width: "220px",
      content: event => {
        const eventSource =
          !event.eventSource || event.eventSource === "unknown"
            ? "Unknown"
            : event.eventSource;

        return <Typography variant="body2">{eventSource}</Typography>;
      },
    },
    {
      title: "Emission Type",
      width: "160px",
      content: event => (
        <TableCellTruncated
          clickableCursor
          tooltipText={event.emissionType}
          text={event.emissionType}
          TypographyProps={{ variant: "body2" }}
        />
      ),
    },
  ];

  // Only add more actions column if user is not a VERIFIER
  const moreActionsColumn: EventTableColumn<EventViewModel> = {
    title: "",
    width: "40px",
    content: () => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          height: "100%",
        }}
      >
        <IconButton aria-label="more actions" onClick={onMoreActionsClick}>
          <MoreVertIcon />
        </IconButton>
      </Box>
    ),
  };

  // Assemble columns in the correct order
  const columns = isVerifier
    ? [...coreColumns, ...dataColumns]
    : isReadOnly
    ? [...coreColumns, ...feedbackColumns, ...dataColumns]
    : [...coreColumns, ...feedbackColumns, ...dataColumns, moreActionsColumn];

  return { columns };
};
